import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePostAdminTenantsTenantIdProjects } from '@/__generated__/admin/admin';
import { ProjectType } from '@/__generated__/model/projectType';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { isAxiosError } from 'axios';

const formSchema = z.object({
  display_name: z.string().min(1, '表示名は必須です'),
  project_type: z
    .nativeEnum(ProjectType)
    .refine(type => type !== ProjectType.UNSPECIFIED, 'タイプを選択してください'),
});

type FormValues = z.infer<typeof formSchema>;

export function CreateProjectDialog({ onSuccess }: { onSuccess: () => void }) {
  const { tenantId } = useParams();
  const [isOpen, setIsOpen] = React.useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      display_name: '',
      project_type: ProjectType.CHAT,
    },
  });

  const { isSubmitting } = form.formState;
  const { trigger: createProject } = usePostAdminTenantsTenantIdProjects(String(tenantId));

  const onSubmit = async (values: FormValues) => {
    try {
      await createProject({
        display_name: values.display_name,
        project_type: values.project_type,
      });
      setIsOpen(false);
      form.reset();
      toast({
        title: 'プロジェクトを作成しました',
        variant: 'default',
      });
      onSuccess();
    } catch (error) {
      if (isAxiosError(error)) {
        toast({
          title: 'プロジェクトの作成に失敗しました',
          description: error.response?.data.message,
          variant: 'destructive',
        });
      }
      console.error('Failed to create project:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>プロジェクトを作成</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>プロジェクトを作成</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>表示名</FormLabel>
                  <FormControl>
                    <Input placeholder="プロジェクトの表示名を入力" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="project_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>タイプ</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="タイプ" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(ProjectType)
                        .filter(type => type !== ProjectType.UNSPECIFIED)
                        .map(type => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? '作成中...' : '作成'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
